import type { ColorScheme, TelegramThemeParams } from "./types";

interface TelegramBackButton {
  hide: () => void;
  offClick?: (callback: () => void) => void;
  onClick?: (callback: () => void) => void;
  show: () => void;
}

interface TelegramHapticFeedback {
  impactOccurred?: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
  notificationOccurred?: (type: "error" | "success" | "warning") => void;
}

interface TelegramWebApp {
  BackButton?: TelegramBackButton;
  HapticFeedback?: TelegramHapticFeedback;
  colorScheme?: ColorScheme;
  expand?: () => void;
  initData: string;
  initDataUnsafe?: {
    start_param?: string;
  };
  offEvent?: (event: "themeChanged" | "viewportChanged", callback: () => void) => void;
  onEvent?: (event: "themeChanged" | "viewportChanged", callback: () => void) => void;
  openTelegramLink?: (url: string) => void;
  platform?: string;
  ready?: () => void;
  setBackgroundColor?: (color: string) => void;
  setHeaderColor?: (color: string) => void;
  themeParams?: TelegramThemeParams;
  version?: string;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function resolveLaunchToken(webApp: TelegramWebApp | null): string | undefined {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.get("launch") ??
    searchParams.get("launchToken") ??
    undefined
  );
}

export function openBotLink(botUsername: string, webApp: TelegramWebApp | null): void {
  if (!botUsername) {
    return;
  }

  const url = `https://t.me/${botUsername}`;
  if (webApp?.openTelegramLink) {
    webApp.openTelegramLink(url);
    return;
  }

  window.location.href = url;
}

export function initializeTelegram(
  webApp: TelegramWebApp | null,
  handlers: { onBack: () => void; onThemeChange: (theme: TelegramThemeParams | undefined, scheme: ColorScheme) => void },
): void {
  if (!webApp) {
    handlers.onThemeChange(undefined, "light");
    return;
  }

  const apply = () => handlers.onThemeChange(webApp.themeParams, webApp.colorScheme ?? "light");

  webApp.ready?.();
  webApp.expand?.();
  apply();

  webApp.onEvent?.("themeChanged", apply);
  webApp.BackButton?.onClick?.(handlers.onBack);
}

export function syncTelegramChrome(webApp: TelegramWebApp | null): void {
  if (!webApp) {
    return;
  }

  const root = document.documentElement;
  const pageBg = getComputedStyle(root).getPropertyValue("--page-bg").trim();
  const headerBg = getComputedStyle(root).getPropertyValue("--header-bg").trim();

  if (pageBg) {
    webApp.setBackgroundColor?.(pageBg);
  }

  if (headerBg) {
    webApp.setHeaderColor?.(headerBg);
  }
}

export function syncTelegramBackButton(webApp: TelegramWebApp | null, shouldShow: boolean): void {
  if (!webApp?.BackButton) {
    return;
  }

  if (shouldShow) {
    webApp.BackButton.show();
    return;
  }

  webApp.BackButton.hide();
}

export function haptic(
  webApp: TelegramWebApp | null,
  tone: "light" | "success" | "warning" | "error",
): void {
  if (!webApp?.HapticFeedback) {
    return;
  }

  if (tone === "light") {
    webApp.HapticFeedback.impactOccurred?.("light");
    return;
  }

  webApp.HapticFeedback.notificationOccurred?.(tone);
}
